import { useCallback, useMemo, useState } from 'react'
import SortOptionTransport from '../../../transports/common/sort-option-transport'
import { useSelector } from 'react-redux'
import { getSortOptions } from '../../../redux/filters/selectors'
import { getIsFilterLoading, smartSearchSubmit } from '../../../redux/filters'
import { useAppDispatch } from '../../../redux/hooks'
import { useRequestUserConfigContext } from '../../../hooks/request'
import AnalyticsService from '../../../services/analytics-service'
import { useABVariant } from '../../../utils/hooks'
import { AB_KEY, AB_VARIANT_VERSION } from '../../../common/constants'

const DEFAULT_SORT_LABEL = 'sort-default'

interface ActiveSortInfo {
    activeSortOption: SortOptionTransport | undefined
    isNonDefaultSortActive: boolean
}

export const useActiveSortOption = (): ActiveSortInfo => {
    const sortOptions = useSelector(getSortOptions)

    return useMemo(() => {
        const activeSortOption = sortOptions?.find(option => option.isActive)
        const isNonDefaultSortActive = !!activeSortOption && activeSortOption.label !== DEFAULT_SORT_LABEL

        return {
            activeSortOption,
            isNonDefaultSortActive,
        }
    }, [sortOptions])
}

interface SmartSearchCallbacks {
    onSuccess: () => void
    onError: () => void
}

interface SmartSearch {
    text: string
    setText: (value: string) => void
    hasError: boolean
    isLoading: boolean
    canSubmit: boolean
    submit: () => void
}

export const useSmartSearch = ({ onSuccess, onError }: SmartSearchCallbacks): SmartSearch => {
    const dispatch = useAppDispatch()
    const user = useRequestUserConfigContext()
    const isLoading = useSelector(getIsFilterLoading)

    const [text, setTextValue] = useState('')
    const [hasError, setHasError] = useState(false)

    const setText = useCallback((value: string) => {
        setTextValue(value)
        setHasError(false)
    }, [])

    const canSubmit = text.trim().length > 0 && !isLoading && !hasError

    const submit = useCallback(async () => {
        const query = text.trim()

        if (!query || isLoading) {
            return
        }

        const { matched, objectCount } = await dispatch(smartSearchSubmit(query, user))

        AnalyticsService.trackEvent({
            action: 'click',
            category: 'smart_search',
            label: query,
            smartSearchMatched: matched,
            objectCount,
        })

        setHasError(!matched)

        if (matched) {
            onSuccess()
        } else {
            onError()
        }
    }, [dispatch, isLoading, onError, onSuccess, text, user])

    return { text, setText, hasError, isLoading, canSubmit, submit }
}

export const useSmartSearchActive = (): boolean => {
    const { isProvidedVariantActive } = useABVariant(AB_KEY.SMART_SEARCH, AB_VARIANT_VERSION.ONE)
    return isProvidedVariantActive
}
